"use client";

import { useConsent } from "@packages/consent";
import { useSearch } from "@packages/framework";
import {
  getCookieAjsAnonymousId,
  getDeviceType,
  getDeviceUserAgent,
  getFullName,
  setCookieAjsAnonymousId,
  useRequestGeolocation,
  useRequestMe,
  useSession,
} from "@packages/sdk";
import type { LDContext } from "launchdarkly-react-client-sdk";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import type { ComponentProps } from "react";
import { useEffect, useMemo, useState } from "react";

import { useMixpanel } from "../hooks/useMixpanel";
import { abContext } from "../lib/abContext";
import { getSearchAttributes } from "../utils/getSearchAttributes";

type LDContexts = Partial<Record<"device" | "guest" | "user", LDContext>>;

type ABContextProviderProps = Omit<
  ComponentProps<typeof abContext.Provider>,
  "value"
>;

export const ABContextProvider = ({
  children,
  ...props
}: ABContextProviderProps) => {
  const flags = useFlags();
  const ldClient = useLDClient();
  const search = useSearch();
  const { status } = useSession();
  const [isIdentifyIsReady, setIsIdentifyIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  /**
   * Keep track of what contexts have been identified
   * ldClient.identify() requires explicit contexts
   */
  const [seenAsDevice, setSeenAsDevice] = useState(false);
  const [seenAsGuest, setSeenAsGuest] = useState(false);
  const [seenAsUser, setSeenAsUser] = useState(false);
  const mixpanel = useMixpanel();

  const { isConsenting, isLoading: isLoadingConsent, isError } = useConsent();
  const { query: queryMe } = useRequestMe();
  const { query: queryGeolocation } = useRequestGeolocation();

  const ldContexts = useMemo<LDContexts>(() => {
    const contexts: LDContexts = {};
    const ldContextAttributesGlobal = {
      platform: "web",
    };
    const isWindowExists = typeof window !== "undefined";

    /**
     * Without consent, LD may initialize before Segment
     * In this case, ONLY create a LD anonymous device context
     * Which will auto-generate a UUID to use as "ajs_anonymous_id" cookie
     * That Segment will use when it initializes
     */
    if (seenAsDevice)
      contexts.device = {
        anonymous: true,
        ...ldContextAttributesGlobal,
      };

    if (isConsenting) {
      const ldContextAttributes: LDContext = {
        anonymous: false,
        apiVersion: process.env.DOPPLER_PUBLIC_X_HALLOW_VERSION,
        browser: getDeviceUserAgent(),
        ipCountry: queryMe.data?.country ?? queryGeolocation.data?.country,
        deviceType: getDeviceType(),
        preferredLocales: isWindowExists ? window?.navigator?.languages : [],
        screenHeight: isWindowExists ? window?.innerHeight : 0,
        screenWidth: isWindowExists ? window?.innerWidth : 0,
        ...getSearchAttributes({ search }),
        ...ldContextAttributesGlobal,
      };

      if (seenAsGuest) {
        contexts.guest = {
          ...ldContextAttributes,
        };

        const ajsAnonymousId = getCookieAjsAnonymousId();
        if (ajsAnonymousId) {
          contexts.guest.key = ajsAnonymousId;
        }
      }

      if (seenAsUser)
        contexts.user = {
          key: queryMe.data?.id?.toString(),
          ...ldContextAttributes,
          email: queryMe.data?.email,
          firstName: queryMe.data?.name,
          lastName: queryMe.data?.last_name,
          locale: queryMe.data?.language,
          name: getFullName({
            firstName: queryMe.data?.name,
            lastName: queryMe.data?.last_name,
          }),
        };
    }

    return contexts;
  }, [
    seenAsDevice,
    seenAsGuest,
    seenAsUser,
    queryGeolocation.data?.country,
    queryMe.data,
  ]);

  useEffect(() => {
    if (
      isLoadingConsent ||
      isError ||
      status === "loading" ||
      queryMe.isLoading ||
      mixpanel.isLoading
    )
      return;

    setSeenAsDevice(true);
    setSeenAsGuest(true);
    if (queryMe.data) setSeenAsUser(true);
    setIsIdentifyIsReady(true);
  }, [
    isError,
    status,
    isLoadingConsent,
    queryMe.data,
    queryMe.isLoading,
    mixpanel.isLoading,
  ]);

  useEffect(() => {
    /** Identify all contexts */
    if (isIdentifyIsReady && Object.keys(ldContexts).length > 0) {
      ldClient?.identify({ kind: "multi", ...ldContexts }, null, () => {
        if (isLoading) setIsLoading(false);

        const ajsAnonymousId = getCookieAjsAnonymousId();
        if (!ajsAnonymousId) {
          const ldAnonymousId = localStorage.getItem("ld:$anonUserId");
          if (ldAnonymousId) {
            localStorage.setItem("ajs_anonymous_id", ldAnonymousId);
            setCookieAjsAnonymousId({
              value: ldAnonymousId,
            });
          }
        }
      });
      if (ldContexts.user) {
        mixpanel.updateContext(ldContexts.user);
      } else if (ldContexts.guest) {
        mixpanel.updateContext(ldContexts.guest);
      }
    }
  }, [isIdentifyIsReady, ldClient, ldContexts]);

  return (
    <abContext.Provider value={{ flags, isLoading, ldClient }} {...props}>
      {children}
    </abContext.Provider>
  );
};
